/**
 * <gh-hotspots> — container for product hotspots rendered by snippets/gh-hotspot.liquid.
 *
 * - Click / tap / Enter / Space on a dot toggles its tooltip; on devices with a mouse,
 *   hovering also opens it.
 * - Only one tooltip is open at a time. Esc, a click outside or opening another closes it.
 * - The tooltip is placed next to the dot and moved so it stays inside the container
 *   (flips left/right, shifts up/down).
 * - In the theme editor, selecting a hotspot block opens it.
 * - Hotspots with "Always show labels" (.gh-hotspot--always) keep their label open and are
 *   only positioned: on load, when the container resizes and when the image loads.
 */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

class GhHotspots extends HTMLElement {
  connectedCallback() {
    this.current = null;
    this.closeTimer = null;

    this.onClick = (event) => {
      const dot = event.target.closest('[data-gh-hotspot-dot]');
      if (!dot || !this.contains(dot)) return;
      const hotspot = dot.closest('[data-gh-hotspot]');
      if (this.current === hotspot && !hotspot.dataset.openedByHover) this.close();
      else this.open(hotspot);
    };

    this.onPointerOver = (event) => {
      if (!canHover.matches) return;
      const hotspot = event.target.closest('[data-gh-hotspot]');
      if (!hotspot || !this.contains(hotspot) || this.isAlways(hotspot)) return;
      clearTimeout(this.closeTimer);
      if (this.current !== hotspot) this.open(hotspot, { hover: true });
    };

    this.onPointerOut = (event) => {
      if (!canHover.matches || !this.current?.dataset.openedByHover) return;
      const hotspot = event.target.closest('[data-gh-hotspot]');
      if (!hotspot || hotspot.contains(event.relatedTarget)) return;
      this.closeTimer = setTimeout(() => this.close(), 200);
    };

    this.onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.current) return;
      const dot = this.current.querySelector('[data-gh-hotspot-dot]');
      this.close();
      dot?.focus();
    };

    this.onDocumentClick = (event) => {
      if (this.current && !this.current.contains(event.target)) this.close();
    };

    this.onResize = () => {
      if (this.current) this.position(this.current);
      this.positionAlways();
    };

    this.onBlockSelect = (event) => {
      const hotspot = this.querySelector(`[data-gh-hotspot][data-block-id="${event.detail.blockId}"]`);
      if (hotspot && !this.isAlways(hotspot)) this.open(hotspot);
    };
    this.onBlockDeselect = () => this.close();

    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    if (this.querySelector('.gh-hotspot--always')) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      this.querySelectorAll('img').forEach((img) => img.addEventListener('load', this.onResize, { once: true }));
      this.positionAlways();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.resizeObserver?.disconnect();
  }

  isAlways(hotspot) {
    return hotspot.classList.contains('gh-hotspot--always');
  }

  positionAlways() {
    this.querySelectorAll('.gh-hotspot--always').forEach((hotspot) => this.position(hotspot));
  }

  open(hotspot, { hover = false } = {}) {
    if (this.current && this.current !== hotspot) this.close();

    const dot = hotspot.querySelector('[data-gh-hotspot-dot]');
    const tooltip = hotspot.querySelector('[data-gh-hotspot-tooltip]');
    if (!dot || !tooltip) return;

    if (hover) hotspot.dataset.openedByHover = 'true';
    else delete hotspot.dataset.openedByHover;

    tooltip.hidden = false;
    hotspot.classList.add('is-open');
    dot.setAttribute('aria-expanded', 'true');
    this.current = hotspot;
    this.position(hotspot);
  }

  close() {
    const hotspot = this.current;
    if (!hotspot) return;
    clearTimeout(this.closeTimer);

    const tooltip = hotspot.querySelector('[data-gh-hotspot-tooltip]');
    const dot = hotspot.querySelector('[data-gh-hotspot-dot]');
    if (tooltip) tooltip.hidden = true;
    dot?.setAttribute('aria-expanded', 'false');
    hotspot.classList.remove('is-open');
    delete hotspot.dataset.openedByHover;
    this.current = null;
  }

  /** Places the tooltip beside the dot and keeps it inside this container. */
  position(hotspot) {
    const tooltip = hotspot.querySelector('[data-gh-hotspot-tooltip]');
    const dot = hotspot.querySelector('[data-gh-hotspot-dot-visual]') || hotspot.querySelector('[data-gh-hotspot-dot]');
    if (!tooltip || !dot) return;

    const pad = 8;
    const gap = 8;
    const bounds = this.getBoundingClientRect();
    const origin = hotspot.getBoundingClientRect();
    const d = dot.getBoundingClientRect();
    const t = { width: tooltip.offsetWidth, height: tooltip.offsetHeight };

    // Horizontal: left of the dot like in Figma (or right with data-gh-hotspot-side="right");
    // flip to the other side when there is no room.
    let left;
    if (hotspot.dataset.ghHotspotSide === 'right') {
      left = d.right + gap;
      if (left + t.width > bounds.right - pad) left = d.left - gap - t.width;
    } else {
      left = d.left - gap - t.width;
      if (left < bounds.left + pad) left = d.right + gap;
    }
    if (left + t.width > bounds.right - pad) left = Math.max(bounds.left + pad, bounds.right - pad - t.width);

    // Vertical: centered on the dot, shifted to stay inside.
    let top = d.top + d.height / 2 - t.height / 2;
    top = Math.min(Math.max(top, bounds.top + pad), bounds.bottom - pad - t.height);

    tooltip.style.left = `${left - origin.left}px`;
    tooltip.style.top = `${top - origin.top}px`;
    tooltip.style.right = 'auto';
    tooltip.style.translate = 'none';
  }
}

if (!customElements.get('gh-hotspots')) customElements.define('gh-hotspots', GhHotspots);
